import React from "react";
import {
    Alert,
    Button,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Popover,
    Skeleton,
    TextField,
    Tooltip,
} from "@mui/material";
import {SearchOutlined} from "@mui/icons-material";
import {formatAmount, formatDate} from "../contractExplorerTypes.ts";
import {
    honouredExtraColumns,
    useExtraPartColumns,
    useLinkCandidatesQuery,
    useManualLinkMutation,
} from "../useContractLinksQueries.ts";

/** Same as the supplier picker, so typing feels identical across the tab. */
const SEARCH_DEBOUNCE_MS = 300;

const ALREADY_LINKED_LABEL: Record<string, string> = {
    linked: 'already linked here',
    accepted: 'already accepted here',
    claimed: 'already claimed here',
    candidate: 'already a candidate here',
    competing: 'already competing here',
    rejected: 'rejected here — use Accept in that section',
};

type Props = {
    bagId: number;
    contractId: number;
    /** Names the scope checkbox; absent when the contract has no supplier match. */
    matchedSupplierName?: string;
};

/**
 * Link a transaction the matcher never proposed, by searching for it.
 *
 * Reviewers know descriptions, suppliers and PO numbers — not transaction ids —
 * so this searches the identifying columns and defaults to the contract's
 * matched supplier, which is the set the matcher itself considers.
 */
export const LinkTransactionPicker: React.FC<Props> = ({bagId, contractId, matchedSupplierName}) => {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    const [search, setSearch] = React.useState('');
    const [debounced, setDebounced] = React.useState('');
    const [allSuppliers, setAllSuppliers] = React.useState(false);
    const manualLink = useManualLinkMutation();

    React.useEffect(() => {
        const timer = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const open = Boolean(anchor);
    const configuredColumns = useExtraPartColumns();
    const {data, isFetching, isError, error} = useLinkCandidatesQuery(
        bagId, contractId, debounced, allSuppliers || !matchedSupplierName, open,
        configuredColumns.map(column => column.key),
    );
    // One DRF page; `count` is the true number of matches.
    const rows = data?.results ?? [];
    const total = data?.count ?? 0;
    // The server's allowlist wins: show only the extras it honoured.
    const extraColumns = honouredExtraColumns(configuredColumns, rows);

    const close = () => {
        setAnchor(null);
        setSearch('');
        setDebounced('');
    };

    const capped = total > rows.length;

    return (
        <>
            <Button variant="outlined" size="small" onClick={e => setAnchor(e.currentTarget)}>
                Link transaction
            </Button>

            <Popover
                open={open}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}>
                <div className="cl-link-picker">
                    <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search description, transaction id, PO number, GL name…"
                        slotProps={{input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small"/>
                                </InputAdornment>
                            ),
                        }}}
                    />

                    <div className="cl-link-scope">
                        {matchedSupplierName && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!allSuppliers}
                                        onChange={e => setAllSuppliers(!e.target.checked)}
                                    />
                                }
                                label={`Only ${matchedSupplierName}'s transactions`}
                            />
                        )}
                        <span className="cl-link-count">
                            {isFetching ? 'searching…' : `${rows.length} of ${total} shown`}
                        </span>
                    </div>

                    {isError && <Alert severity="error">Could not search transactions: {error?.message}</Alert>}
                    {manualLink.error && <Alert severity="error">{manualLink.error.message}</Alert>}

                    <div className="cl-link-results">
                        {isFetching && rows.length === 0 && <Skeleton variant="rectangular" height={160}/>}
                        {!isFetching && rows.length === 0 && (
                            <p className="cd-empty">No transactions match this search.</p>
                        )}
                        {rows.map(row => (
                            <div key={row.part_id} className="cl-link-row">
                                <span className="cl-link-main">
                                    <span className="cl-link-desc">{row.description || row.p_id}</span>
                                    <span className="cd-sub">
                                        {[row.supplier, row.po_number && `PO ${row.po_number}`, row.p_id]
                                            .filter(Boolean).join(' · ')}
                                    </span>
                                </span>
                                {extraColumns.map(column => (
                                    <span key={column.key} className="cl-link-extra"
                                          title={`${column.label}: ${row.extra?.[column.key] ?? '—'}`}>
                                        {row.extra?.[column.key] ?? '—'}
                                    </span>
                                ))}
                                <span className="cl-link-date">{formatDate(row.date)}</span>
                                <span className="cl-link-spend">{formatAmount(row.spend)}</span>
                                <span className="cl-link-action">
                                    {row.link_status
                                        ? (
                                            <Tooltip title={ALREADY_LINKED_LABEL[row.link_status] ?? row.link_status}>
                                                <span className="cl-link-already">
                                                    {ALREADY_LINKED_LABEL[row.link_status] ?? row.link_status}
                                                </span>
                                            </Tooltip>
                                        )
                                        : (
                                            <Button
                                                size="small"
                                                disabled={manualLink.isPending}
                                                onClick={() => manualLink.mutate(
                                                    {bagId, contractId, partId: row.part_id},
                                                    {onSuccess: close},
                                                )}>
                                                Link
                                            </Button>
                                        )}
                                </span>
                            </div>
                        ))}
                    </div>

                    {capped && (
                        <p className="cl-link-footer">
                            Showing the top {rows.length} by spend of {total} matches — refine the search to
                            see the rest.
                        </p>
                    )}
                </div>
            </Popover>
        </>
    );
};
